/**
 * useKeyboardTop — the soft keyboard's top edge in WINDOW coordinates, or
 * null when no soft keyboard is up. Used to dock the inline game chat (map
 * controls bar + recent chat lines + input) directly on the keyboard.
 *
 *   NATIVE  Keyboard show/hide events (`endCoordinates.screenY`). iOS uses the
 *           will* events so the dock moves with the keyboard; Android only
 *           fires did*. Works whether or not Android resizes the window
 *           (adjustResize): the dock offset is computed against the measured
 *           container bottom, so a resized window simply yields offset 0.
 *   WEB     Touch devices only (desktop has no soft keyboard): the visual
 *           viewport's bottom edge. iOS Safari shrinks only the visual
 *           viewport; Android Chrome (interactive-widget=resizes-content)
 *           shrinks the layout too — both give the right bottom edge.
 *
 * Native-safe: every web global is read behind Platform/typeof guards.
 */
import { useEffect, useState } from 'react'
import { Keyboard, Platform, StatusBar } from 'react-native'
import { isCoarsePointerWeb } from '../lib/platform'

/**
 * Reset the scroll of every element that contains the focused field (web).
 * Starts at its parent: the field itself scrolls legitimately once a long
 * message passes the 3-line cap.
 */
export function unscrollFocusedAncestors(): void {
  if (typeof document === 'undefined') return
  let node: Element | null = document.activeElement?.parentElement ?? null
  while (node && node !== document.documentElement) {
    if (node.scrollTop !== 0) node.scrollTop = 0
    node = node.parentElement
  }
  const root = document.scrollingElement
  if (root && root.scrollTop !== 0) root.scrollTop = 0
}

/**
 * The native keyboard's top edge in measureInWindow coordinates. Android
 * reports it from the top of the screen while measureInWindow measures from
 * below the status bar, so subtract the status bar there (iOS uses one origin
 * for both). Without this the chat dock fell short by the status bar's height
 * and the input stayed behind the keyboard on tall maps.
 */
export function keyboardTopInWindow(screenY: number, os: string, statusBarHeight: number | undefined): number {
  return os === 'android' ? screenY - (statusBarHeight ?? 0) : screenY
}

export function useKeyboardTop(enabled = true): number | null {
  const [top, setTop] = useState<number | null>(null)

  useEffect(() => {
    if (!enabled) {
      setTop(null)
      return
    }
    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || !isCoarsePointerWeb()) return
      const viewport = window.visualViewport
      if (!viewport) return
      const update = () => {
        // Mobile browsers scroll the page to bring a focused field into view,
        // which pushed the game's top bar off-screen. The map shrinks to make
        // room instead, so keep the page pinned at the top while chat is open.
        if (window.scrollY !== 0 || window.pageYOffset !== 0) window.scrollTo(0, 0)
        unscrollFocusedAncestors()
        setTop(viewport.offsetTop + viewport.height)
      }
      // Android Chrome reveals a focused field low on a tall map by scrolling
      // an INNER container (even overflow:hidden ones), not the window, so the
      // window pin above never fired and the whole screen rode up — top bar
      // out of view, and a gap above the keyboard. Scroll events don't bubble:
      // catch them in the capture phase and re-pin any container holding the
      // focused field. Real scrollers (the chat log) never contain the input.
      const onAnyScroll = (event: Event) => {
        const target = event.target
        const focused = document.activeElement
        if (target instanceof Element && target !== focused && target.contains(focused) && target.scrollTop !== 0) {
          update()
        }
      }
      update()
      viewport.addEventListener('resize', update)
      viewport.addEventListener('scroll', update)
      document.addEventListener('scroll', onAnyScroll, true)
      return () => {
        viewport.removeEventListener('resize', update)
        viewport.removeEventListener('scroll', update)
        document.removeEventListener('scroll', onAnyScroll, true)
      }
    }
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'
    // Android reports the keyboard top from the top of the SCREEN, but
    // measureInWindow (what the dock compares it with) measures from below
    // the status bar, so the dock fell short by exactly the status bar's
    // height and the chat input stayed behind the keyboard on tall maps.
    // Convert to the measurement's coordinates. iOS uses one origin for both.
    const show = Keyboard.addListener(showEvent, (event) =>
      setTop(keyboardTopInWindow(event.endCoordinates.screenY, Platform.OS, StatusBar.currentHeight)))
    const hide = Keyboard.addListener(hideEvent, () => setTop(null))
    return () => {
      show.remove()
      hide.remove()
    }
  }, [enabled])

  return top
}
